/**
 * Account menu in the header. Replaces the standalone role badge + theme
 * switcher: shows an avatar trigger and a dropdown with the user's name/role,
 * presence status, quick links (Settings, Notifications, start a chat), inline
 * theme options, an enable-notifications action, and logout.
 *
 * Lives in the app layer (not `shared`) because it composes feature concerns
 * (auth, presence, p2p-chat) and may only be wired from the app layer.
 *
 * @module app/layouts/UserMenu
 */

import {
  Bell,
  Heart,
  LogOut,
  MessageCircle,
  Monitor,
  Moon,
  Settings as SettingsIcon,
  Star,
  Sun,
  Users,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { RoleIndicator } from '@/features/auth/components/RoleIndicator';
import { useAuth } from '@/features/auth/hooks/useAuth';
import { startChatSession } from '@/features/p2p-chat';
import { CHAT_CAP_TOAST } from '@/features/p2p-chat/constants/chatToasts';
import { useFavoriteContact } from '@/features/p2p-chat/hooks/useFavoriteContact';
import { sendPresenceInvite } from '@/features/p2p-chat/lib/presence';
import { generateRoomCode } from '@/features/p2p-chat/lib/roomCode';
import {
  selectMemberStatus,
  useFamilyPresenceStore,
} from '@/features/p2p-chat/stores/familyPresenceStore';
import { useP2PChatStore } from '@/features/p2p-chat/stores/p2pChatStore';
import { Avatar, AvatarFallback } from '@/shared/components/ui/avatar';
import { Button } from '@/shared/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/shared/components/ui/dropdown-menu';
import {
  PRESENCE_DOT_CLASS,
  PRESENCE_LABEL,
} from '@/shared/components/ui/presence-dot';
import { getPath, RouteKey } from '@/shared/constants/routes';
import { useBrowserNotifications } from '@/shared/hooks/useBrowserNotifications';
import { toast } from '@/shared/hooks/useToast';
import { cn } from '@/shared/lib/utils';
import { useThemeStore, THEMES, type Theme } from '@/shared/stores/themeStore';
import { ButtonSize, ButtonVariant } from '@/shared/types/button.types';

const THEME_META: Readonly<Record<Theme, { label: string; icon: typeof Sun }>> =
  {
    light: { label: 'Светла', icon: Sun },
    dark: { label: 'Тъмна', icon: Moon },
    madivladi: { label: 'Мади и Влади', icon: Heart },
    system: { label: 'Системна', icon: Monitor },
  };

function initials(name: string): string {
  return name.trim().slice(0, 2).toUpperCase();
}

export function UserMenu() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  // Supabase-sourced presence (AC-13c): own status + the favorite's status.
  const myStatus = useFamilyPresenceStore((s) => s.myStatus);
  const statuses = useFamilyPresenceStore((s) => s.statuses);
  const setForeground = useP2PChatStore((s) => s.setForeground);
  const { favorite } = useFavoriteContact();
  const theme = useThemeStore((s) => s.theme);
  const setTheme = useThemeStore((s) => s.setTheme);
  const { supported, permission, requestPermission } =
    useBrowserNotifications();

  if (!user) return null;

  const handleLogout = (): void => {
    void logout();
    void navigate(getPath(RouteKey.LOGIN));
  };

  const handleInviteFavorite = (): void => {
    if (!favorite) {
      void navigate(getPath(RouteKey.P2P_CHAT));
      return;
    }
    const code = generateRoomCode();
    const result = startChatSession({
      roomCode: code,
      initiator: true,
      peerId: favorite.profileId,
      peerName: favorite.displayName,
    });

    if (result.kind === 'cap') {
      toast(CHAT_CAP_TOAST);
      void navigate(getPath(RouteKey.P2P_CHAT));
      return;
    }
    if (result.kind === 'duplicate') {
      setForeground(result.existingId);
      void navigate(getPath(RouteKey.P2P_CHAT));
      return;
    }

    const reachable =
      selectMemberStatus(statuses, favorite.profileId) !== 'offline';
    sendPresenceInvite(favorite.profileId, code);
    toast({
      title: reachable ? 'Поканата е изпратена' : 'Поканата е в опашката',
      description: reachable
        ? `${favorite.displayName} ще получи известие веднага.`
        : `${favorite.displayName} ще получи поканата при следващото влизане.`,
    });
    void navigate(getPath(RouteKey.P2P_CHAT));
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant={ButtonVariant.Ghost}
          size={ButtonSize.Icon}
          className="relative size-9 rounded-full"
          aria-label="Профил меню"
        >
          <Avatar className="size-9">
            <AvatarFallback>{initials(user.name)}</AvatarFallback>
          </Avatar>
          <span
            className={cn(
              'absolute bottom-0 right-0 h-2.5 w-2.5 rounded-full border-2 border-card',
              PRESENCE_DOT_CLASS[myStatus]
            )}
            aria-hidden="true"
          />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-60">
        <DropdownMenuLabel className="flex flex-col gap-2">
          <span className="truncate font-medium">{user.name}</span>
          <span className="flex items-center justify-between gap-2">
            <RoleIndicator compact />
            <span className="text-xs text-muted-foreground">
              {PRESENCE_LABEL[myStatus]}
            </span>
          </span>
        </DropdownMenuLabel>

        <DropdownMenuSeparator />

        <DropdownMenuItem
          className="cursor-pointer gap-2"
          onClick={() => void navigate(getPath(RouteKey.P2P_CHAT))}
        >
          <MessageCircle className="size-4" aria-hidden="true" />
          Започни чат
        </DropdownMenuItem>
        <DropdownMenuItem
          className="cursor-pointer gap-2"
          onClick={handleInviteFavorite}
        >
          <Star className="size-4" aria-hidden="true" />
          {favorite ? `Покани ${favorite.displayName}` : 'Избери любим контакт'}
          {favorite && (
            <span
              className={cn(
                'ml-auto h-2.5 w-2.5 rounded-full',
                PRESENCE_DOT_CLASS[
                  selectMemberStatus(statuses, favorite.profileId)
                ]
              )}
              aria-hidden="true"
            />
          )}
        </DropdownMenuItem>
        <DropdownMenuItem
          className="cursor-pointer gap-2"
          onClick={() => void navigate(getPath(RouteKey.NOTIFICATIONS))}
        >
          <Bell className="size-4" aria-hidden="true" />
          Известия
        </DropdownMenuItem>
        <DropdownMenuItem
          className="cursor-pointer gap-2"
          onClick={() => void navigate(getPath(RouteKey.FAMILY_SETTINGS))}
        >
          <Users className="size-4" aria-hidden="true" />
          Семейство
        </DropdownMenuItem>
        <DropdownMenuItem
          className="cursor-pointer gap-2"
          onClick={() => void navigate(getPath(RouteKey.SETTINGS))}
        >
          <SettingsIcon className="size-4" aria-hidden="true" />
          Настройки
        </DropdownMenuItem>

        {supported && permission !== 'granted' && permission !== 'denied' && (
          <DropdownMenuItem
            className="cursor-pointer gap-2"
            onClick={() => void requestPermission()}
          >
            <Bell className="size-4" aria-hidden="true" />
            Включи известията
          </DropdownMenuItem>
        )}

        <DropdownMenuSeparator />
        <DropdownMenuLabel className="text-xs text-muted-foreground">
          Тема
        </DropdownMenuLabel>
        {THEMES.map((option) => {
          const meta = THEME_META[option];
          const Icon = meta.icon;
          return (
            <DropdownMenuItem
              key={option}
              className="cursor-pointer gap-2"
              onClick={() => setTheme(option)}
            >
              <Icon className="size-4" aria-hidden="true" />
              <span>{meta.label}</span>
              {theme === option && (
                <span className="ml-auto text-xs text-muted-foreground">✓</span>
              )}
            </DropdownMenuItem>
          );
        })}

        <DropdownMenuSeparator />
        <DropdownMenuItem
          className="cursor-pointer gap-2 text-destructive focus:text-destructive"
          onClick={handleLogout}
        >
          <LogOut className="size-4" aria-hidden="true" />
          Изход
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
